import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import type { SortDirectionType } from "@/types/common/filters";
import axios from "@/utils/axios";
export interface EmailTemplateVariable {
  type: string;
  required: boolean;
  description: string;
}

export interface EmailTemplate {
  id: string;
  type: string | null;
  name: string;
  description: string | null;
  subject: string;
  rich_text: string;
  variables: Record<string, EmailTemplateVariable>;
  status: string;
  created_at: string;
  updated_at: string;
  required_variables: string[];
  optional_variables: string[];
}

export interface GetEmailTemplatesParams {
  page: number;
  per_page: number;
  order_by?: string;
  sort_direction?: SortDirectionType;
  search?: string;
  filters?: {
    name?: string;
    status?: string;
    search?: string;
  };
}

export interface CreateEmailTemplateInput {
  name: string;
  description?: string;
  subject: string;
  rich_text: string;
  status?: string;
}

export interface UpdateEmailTemplateInput {
  name?: string;
  description?: string;
  subject?: string;
  rich_text?: string;
  status?: string;
}

export interface PreviewEmailTemplateInput {
  data?: Record<string, string>;
}

export interface PreviewEmailTemplateResponse {
  data: {
    template: {
      id: string;
      name: string;
    };
    rendered: {
      subject: string;
      body: string;
    };
    data_used: Record<string, string>;
    vars_used: string[];
    vars_missing: string[];
  };
}

export interface EmailTemplateResponse {
  message: string;
  data: EmailTemplate;
}

interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

// API base path
const API_BASE_PATH = "/admin/v1/email-templates";

// List email templates
export const useGetEmailTemplates = (params: GetEmailTemplatesParams) => {
  return useQuery({
    queryKey: ["email-templates", params],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PaginatedResponse<EmailTemplate>> => {
      const { page, per_page, order_by, sort_direction, search, filters } =
        params;

      const queryParams: Record<string, string | number | undefined> = {
        ...(page ? { page } : {}),
        ...(per_page ? { per_page } : {}),
        ...(order_by
          ? {
              order_by: sort_direction === "desc" ? `!${order_by}` : order_by,
            }
          : {}),
      };

      // Add filters as filter[key] format
      if (filters) {
        if (filters.name) {
          queryParams["filter[name]"] = filters.name;
        }
        if (filters.status) {
          queryParams["filter[status]"] = filters.status;
        }
        if (filters.search) {
          queryParams["filter[search]"] = filters.search;
        }
      }

      // Global search parameter
      if (search) {
        queryParams["filter[search]"] = search;
      }

      const response = await axios.get<PaginatedResponse<EmailTemplate>>(
        API_BASE_PATH,
        { params: queryParams },
      );
      return response.data;
    },
  });
};

// Get single email template
export const useGetEmailTemplate = (id: string) => {
  return useQuery({
    queryKey: ["email-templates", id],
    queryFn: async (): Promise<{ data: EmailTemplate }> => {
      const response = await axios.get<{ data: EmailTemplate }>(
        `${API_BASE_PATH}/${id}`,
      );
      return response.data;
    },
    enabled: !!id,
  });
};

// Create email template
export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: CreateEmailTemplateInput,
    ): Promise<EmailTemplateResponse> => {
      const response = await axios.post<EmailTemplateResponse>(
        API_BASE_PATH,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
    },
  });
};

// Update email template
export const useUpdateEmailTemplate = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: UpdateEmailTemplateInput,
    ): Promise<EmailTemplateResponse> => {
      const response = await axios.put<EmailTemplateResponse>(
        `${API_BASE_PATH}/${id}`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      queryClient.invalidateQueries({ queryKey: ["email-templates", id] });
    },
  });
};

// Delete email template (soft delete)
export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ message: string }> => {
      const response = await axios.delete<{ message: string }>(
        `${API_BASE_PATH}/${id}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
    },
  });
};

// Preview email template
export const usePreviewEmailTemplate = (id: string) => {
  return useMutation({
    mutationFn: async (
      input: PreviewEmailTemplateInput,
    ): Promise<PreviewEmailTemplateResponse> => {
      const response = await axios.post<PreviewEmailTemplateResponse>(
        `${API_BASE_PATH}/${id}/preview`,
        input,
      );
      return response.data;
    },
  });
};

// Preview email template as HTML for iframe
export const usePreviewEmailTemplateHtml = (id: string) => {
  return useMutation({
    mutationFn: async (input: PreviewEmailTemplateInput): Promise<string> => {
      const response = await axios.post<string>(
        `${API_BASE_PATH}/${id}/preview-html`,
        input,
        { headers: { Accept: "text/html" } },
      );
      return response.data;
    },
  });
};

// Activate email template (updates status to active)
export const useActivateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<EmailTemplateResponse> => {
      const response = await axios.put<EmailTemplateResponse>(
        `${API_BASE_PATH}/${id}`,
        { status: "active" },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
    },
  });
};

// Deactivate email template (updates status to inactive)
export const useDeactivateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<EmailTemplateResponse> => {
      const response = await axios.put<EmailTemplateResponse>(
        `${API_BASE_PATH}/${id}`,
        { status: "inactive" },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
    },
  });
};
